<template>
  <Suspense class="page-loader" timeout="0" @pending="onPending" @resolve="onResolve" @fallback="onFallback">
    <slot />
    <template #fallback>
      <AppLoadingIndicator />
    </template>
  </Suspense>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { createError, joinSlugId, useHeadData, useTheme, useTracking } from '#imports'
import { useDomainStore } from '~/store/domain'
import { usePageStore } from '~/store/page'
import { pageDefinitions } from '~/constants/pages'
import { useLoadPage } from '~/composables/useLoadPage'
import { TPage } from '~/types/page-types'
import { useRoutingLogic } from '~/composables/useRoutingLogic'

const domains = useDomainStore()
const pageStore = usePageStore()
const { $preview, $secret } = useNuxtApp()

const emit = defineEmits(['loaded'])

const props = defineProps<{
  collection: string,
  filter?: object,
}>()

const onPending = () => {
}
const onResolve = () => {
}
const onFallback = () => {
}

if (!pageDefinitions[props.collection]) {
  throw createError({
    statusCode: 500,
    statusMessage: `Page collection "${props.collection}" not found.`,
    fatal: true
  })
}

const { data, pending, error } = await useLoadPage(props.collection, props.filter, $preview, $secret)

// redirects, error pages etc.
useRoutingLogic(data, pending, error)

if (data.value) {
  // attempt to add the id to the slug
  if (data.value?.slug && data.value?.id) {
    data.value.slugId = joinSlugId(data.value)
  }

  // write to store (for referencing)
  pageStore.setCurrentPage(data.value as TPage)
  pageStore.setShowProgressBar(pageDefinitions[props.collection].showProgressBar || false)

  // update domain store
  domains.setCurrentDomain(pageStore?.getCurrentPage as TPage)

  if (import.meta.server) {
    useTheme()
  }

  // update head & meta data (including theme switching)
  useHeadData(pageStore.getCurrentPage as TPage)

  // emit an event, which provides the page data to parent (page)component
  emit('loaded', data.value as TPage)
}

onMounted(() => {
  useTracking(pageStore.getCurrentPage)

  window.setTimeout(() => {
    useTheme()
  }, 1500)
})

</script>
